/**
 * **The sloop on screen** — one loaded `.glb` per ship, with its seventeen-node
 * rig driven from the sim's own bearings.
 *
 * The contract this file codes against is
 * [ship-design.md](../../../../docs/engineering/ship-design.md) §4, and nothing
 * here restates a number that document owns: the node names, the parent chain,
 * the axes and the signs all come from §4.1 and §4.2, and the two searchlight
 * facts come from §4.5.
 *
 * ## A scene graph, not an instanced pool
 *
 * `searchlight-mount.ts` instances its three pieces with hand-composed matrices
 * because a night mission puts six identical projectors on the field and a
 * `Group` per light would have bought nothing. A ship is the opposite case in
 * every respect: there are at most three of them
 * (`SHIP_MAX_COUNT`), each carries **five** articulated mounts and **two**
 * searchlights on a real parent chain, and the yoke genuinely has to carry the
 * drum. So each ship is a `clone()` of the loaded model — three.js clones share
 * geometry and material, so the fleet is one upload — and the rig is driven by
 * setting a quaternion on a named node and letting the graph do the rest.
 *
 * ## The signs, which ship-design.md §4.2 warns are the easy thing to get wrong
 *
 * - **Yaw is about +Y and a positive angle swings the muzzle to port.** The sim
 *   hands out a `Traverse` whose azimuth is positive to *starboard*, so the node
 *   angle is its negation. §4.2 states the same rule as "for a bearing measured
 *   clockwise from the ship's head, the node angle is −bearing".
 * - **Pitch is about +X and a positive angle raises the muzzle**, which is the
 *   sim's elevation unchanged.
 *
 * §4.3's warning about the main gun's trunnion — 0.55 m forward of its training
 * axis, so the muzzle is not `pivot + direction × length` — is honoured here by
 * construction rather than by arithmetic: the barrel is a child of
 * `MAIN_PITCH`, so its world transform *is* the answer, and this file never
 * reconstructs a muzzle position at all.
 */

import * as THREE from "three/webgpu";
import { SHIP_MOUNT_NAMES } from "@red-opus/engine";
import { SEA_PLATE_DEPTH_M } from "../world/water-mask.ts";
import { loadEnhancedModel } from "./enhanced-model.ts";
import { setEnsignSide, type EnsignSide } from "./enhanced-nodes.ts";

/**
 * The `y` a hull floats at — **the drawn sea's own surface**, which is a metre
 * below the datum.
 *
 * The model's origin is on its waterline (ship-design.md §3.2) and the sim puts
 * her at `y = 0`, so the obvious thing is to draw her there. That is wrong by
 * exactly the metre `world/water-mask.ts` gives the sea plate: an `s = 10`
 * plate is drawn at `−SEA_PLATE_DEPTH_M` so that water is unambiguously *under*
 * land (`london-sea-placements.md` §8.6). A ship at the datum therefore floats a
 * metre proud of the water she is on, showing a metre of boot-topping and
 * anti-fouling red that no ship at her marks would show — which is exactly what
 * the first cut of the orbit clip caught at 105 m (2026-09-01).
 *
 * So the *renderer* floats her on the surface the renderer drew, while the sim
 * goes on placing her at `y = 0`. The metre never reaches the simulation, and
 * every consequence of it is read off the model's own `matrixWorld` — the
 * searchlight lenses and the muzzles — so nothing in the picture disagrees with
 * anything else in the picture.
 */
export const SHIP_WATERLINE_Y_M = -SEA_PLATE_DEPTH_M;

/** `bun run enhanced` writes it here — `tools/blender/ship.py`, ship-design.md §8. */
const SHIP_MODEL_PATH = "models/ship.glb";

/** The two 90 cm projectors' rig prefixes, forward and aft (ship-design.md §4.1). */
const LIGHT_NAMES = ["LIGHT1", "LIGHT2"] as const;

const YAW_AXIS = new THREE.Vector3(0, 1, 0);
const PITCH_AXIS = new THREE.Vector3(1, 0, 0);
/** Her fore-and-aft line: the bow points −Z (ship-design.md §4), so a roll is about +Z. */
const HEEL_AXIS = new THREE.Vector3(0, 0, 1);
const scratchHeel = new THREE.Quaternion();
const scratchTrim = new THREE.Quaternion();

/** One mounting's two nodes: the one that trains and the one that elevates. */
interface RigMount {
  readonly yaw: THREE.Object3D;
  readonly pitch: THREE.Object3D;
}

function mountNodes(root: THREE.Object3D, prefix: string): RigMount | undefined {
  const yaw = root.getObjectByName(`${prefix}_YAW`);
  const pitch = root.getObjectByName(`${prefix}_PITCH`);
  return yaw && pitch ? { yaw, pitch } : undefined;
}

function lightNodes(root: THREE.Object3D, prefix: string): RigMount | undefined {
  const yaw = root.getObjectByName(`${prefix}_YOKE`);
  const pitch = root.getObjectByName(`${prefix}_DRUM`);
  return yaw && pitch ? { yaw, pitch } : undefined;
}

/**
 * One drawn ship: her hull in the world, her five mountings, and her two lights.
 *
 * Every setter is idempotent and allocation-free, so a frame that calls all of
 * them costs a handful of quaternion writes.
 */
export class ShipModel {
  readonly root: THREE.Object3D;
  /** In `SHIP_MOUNT_NAMES` order, so an index here is an index into the sim's own `ShipAim.mounts`. */
  private readonly mounts: readonly (RigMount | undefined)[];
  private readonly lights: readonly (RigMount | undefined)[];
  /** World-space trunnion of each projector's drum, refreshed on demand for the beams. */
  private readonly lensScratch = new THREE.Vector3();

  constructor(root: THREE.Object3D) {
    this.root = root;
    this.mounts = SHIP_MOUNT_NAMES.map((name) => mountNodes(root, name));
    this.lights = LIGHT_NAMES.map((name) => lightNodes(root, name));
  }

  /**
   * Which ensign flies. **Must be called before the ship is first drawn**:
   * glTF has no visibility of its own, so both flags arrive visible and a ship
   * that has not been told reads as neither (ship-design.md §5).
   */
  setEnsign(side: EnsignSide): number {
    return setEnsignSide(this.root, side);
  }

  /**
   * Puts her on the sea at `(x, z)` heading `headingRad`, heeled `heelRad`,
   * trimmed `trimRad` and `sinkM` metres below her own marks.
   *
   * Her `y` is `SHIP_WATERLINE_Y_M` less `sinkM`, and not the sim's zero — see
   * that constant for the metre and why the renderer owns it.
   *
   * **Both attitudes are applied in her own frame, after the yaw**, so they are
   * a roll about her fore-and-aft line and a pitch about her beam rather than
   * about world axes:
   *
   * - `heelRad` positive lifts the starboard side, which is the *outward* sense
   *   in a turn to starboard — see `flight/scene-ships.ts` for why a ship leans
   *   the opposite way to an aeroplane. Once she is going down it carries her
   *   flooding list as well, which is the same rotation about the same line.
   * - `trimRad` positive **lifts the bow**: `PITCH_AXIS` is +X and her bow is
   *   −Z, so a positive rotation about it swings the stem up. That is the sense
   *   `ship-damage.ts`'s `SHIP_SINK_TRIM_RAD` is stated in — she goes by the
   *   stern.
   *
   * Trim and sink default to zero and heel already did, so a caller with nothing
   * to say about any of them gets exactly the upright pose at her marks that
   * this method produced before there was damage in the port.
   */
  setPose(x: number, z: number, headingRad: number, heelRad = 0, trimRad = 0, sinkM = 0): void {
    this.root.position.set(x, SHIP_WATERLINE_Y_M - sinkM, z);
    this.root.quaternion.setFromAxisAngle(YAW_AXIS, headingRad);
    if (trimRad !== 0) {
      scratchTrim.setFromAxisAngle(PITCH_AXIS, trimRad);
      this.root.quaternion.multiply(scratchTrim);
    }
    if (heelRad !== 0) {
      scratchHeel.setFromAxisAngle(HEEL_AXIS, heelRad);
      this.root.quaternion.multiply(scratchHeel);
    }
  }

  /** Trains and elevates one mounting. `azimuthRad` is the sim's — positive to starboard. */
  aimMount(index: number, azimuthRad: number, elevationRad: number): void {
    const mount = this.mounts[index];
    if (!mount) return;
    mount.yaw.quaternion.setFromAxisAngle(YAW_AXIS, -azimuthRad);
    mount.pitch.quaternion.setFromAxisAngle(PITCH_AXIS, elevationRad);
  }

  /** The same for one of the two projectors. */
  aimLight(index: number, azimuthRad: number, elevationRad: number): void {
    const light = this.lights[index];
    if (!light) return;
    light.yaw.quaternion.setFromAxisAngle(YAW_AXIS, -azimuthRad);
    light.pitch.quaternion.setFromAxisAngle(PITCH_AXIS, elevationRad);
  }

  /**
   * Where one projector's beam leaves the glass, in world metres.
   *
   * Read off the drum node's own `matrixWorld` rather than reconstructed from
   * the ship's pose and a written-down offset, which is what ship-design.md
   * §4.5 asks a consuming lane to do and what keeps the beam on the aperture if
   * the generator ever moves it.
   */
  lensPosition(index: number, into: THREE.Vector3): boolean {
    const light = this.lights[index];
    if (!light) return false;
    light.pitch.updateWorldMatrix(true, false);
    into.setFromMatrixPosition(light.pitch.matrixWorld);
    return true;
  }

  /** The direction that projector is looking, in world metres. */
  lensDirection(index: number, into: THREE.Vector3): boolean {
    const light = this.lights[index];
    if (!light) return false;
    light.pitch.updateWorldMatrix(true, false);
    // The rig's rest direction is the model's own bow, which is −Z.
    into.set(0, 0, -1).transformDirection(light.pitch.matrixWorld).normalize();
    return true;
  }

  /** Frees this ship's clone. Geometry and material are shared with the fleet, so only the fleet's owner disposes them. */
  detach(): void {
    this.root.removeFromParent();
    this.lensScratch.set(0, 0, 0);
  }
}

/**
 * The fleet: one loaded model, `count` clones of it, or `undefined` when there
 * is no model to clone.
 *
 * `data/enhanced` is optional and written by a separate `bun run enhanced`
 * step, so its absence is a normal state rather than an error — the same stance
 * `loadSearchlightMounts` and `enhanced-catalog.ts` take. Without it a sortie
 * flown with ENHANCED SIMULATION on still has ships in the *sim*: they steam,
 * they shoot and their flak bursts draw. What is missing is the hull, and that
 * is the honest failure for a missing asset rather than a silent one.
 */
export class ShipFleet {
  readonly ships: readonly ShipModel[];
  private readonly source: THREE.Object3D;

  constructor(model: THREE.Object3D, count: number) {
    this.source = model;
    this.ships = Array.from({ length: count }, () => new ShipModel(model.clone(true)));
  }

  dispose(): void {
    for (const ship of this.ships) ship.detach();
    this.source.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      node.geometry.dispose();
      // Freeing the material is what releases this render object's bindings
      // under the WebGPU backend (2026-08-19 world-stability lesson).
      (node.material as THREE.Material).dispose();
    });
  }
}

export async function loadShipFleet(count: number): Promise<ShipFleet | undefined> {
  if (count <= 0) return undefined;
  try {
    return new ShipFleet(await loadEnhancedModel(SHIP_MODEL_PATH), count);
  } catch {
    return undefined;
  }
}
